import { User as OidcUser } from "oidc-client-ts";
import User from "@/interfaces/permission/user";
import { AppRole, getAppRoles } from "@/service/AppRoleService";
import Logger from "@/utils/Logger";
import { AppSettingsService } from "./AppSettingsService";

interface AuthUser {
  name: string;
  email: string;
}

export class AuthService {
  static extractUserProfile(oidcUser: OidcUser): AuthUser {
    Logger.debug("Extracting user profile from OIDC user", ["AuthService"]);

    const profile = {
      name: oidcUser.profile.name || oidcUser.profile.given_name || "",
      email: oidcUser.profile.email || "",
    };

    Logger.info("User profile extracted successfully", ["AuthService"]);
    return profile;
  }

  static extractUserInfo(oidcUser: OidcUser): User {
    Logger.debug("Extracting detailed user info from OIDC user", [
      "AuthService",
    ]);

    const userInfo: User = {
      address:
        oidcUser.profile.address && oidcUser.profile.address.country
          ? { country: oidcUser.profile.address.country }
          : undefined,
      appRoles: getAppRoles(oidcUser),
      displayName:
        oidcUser.profile.name ||
        `${oidcUser.profile.given_name} ${oidcUser.profile.family_name}`.trim(),
      destinationIndicator: oidcUser.profile.destinationIndicator as
        | string
        | undefined,
      employeeID: oidcUser.profile.employeeID as string | undefined,
      employeeType: oidcUser.profile.employeeType as string | undefined,
      family_name: oidcUser.profile.family_name,
      given_name: oidcUser.profile.given_name,
      sub: oidcUser.profile.sub,
    };

    Logger.info("User info extracted successfully", ["AuthService"]);
    return userInfo;
  }

  static extractAppRoles(oidcUser: OidcUser): AppRole[] {
    Logger.debug("Extracting app roles from OIDC user", ["AuthService"]);
    const roles = getAppRoles(oidcUser);
    Logger.info(`App roles extracted: ${roles.length} roles found`, [
      "AuthService",
    ]);
    return roles;
  }

  static getDevelopmentUser(): AuthUser {
    Logger.warn(
      "Development mode: Using mock user - bypassing OIDC authentication",
      ["AuthService"],
    );
    return {
      name: "Local Dev User",
      email: "localdev@example.com",
    };
  }

  static getDevelopmentUserInfo(): User {
    Logger.warn(
      "Development mode: Using mock user profile with admin privileges",
      ["AuthService"],
    );
    return {
      appRoles: [AppRole.SCRM_APP_ADMIN],
      displayName: "Local Dev User",
      employeeID: "DEV001",
      employeeType: "Developer",
      family_name: "User",
      given_name: "Local",
      sub: "dev-user-123",
    };
  }

  static getDevelopmentAppRoles(): AppRole[] {
    Logger.warn(
      "Development mode: Granting full admin permissions for local testing",
      ["AuthService"],
    );
    return [AppRole.SCRM_APP_ADMIN];
  }

  static isAuthenticated(user: OidcUser | null): boolean {
    const oauthBypassEnabled =
      AppSettingsService.isOAuthLocalHostBypassEnabled();
    const isLocalhost = window.location.hostname === "localhost";

    if (!oauthBypassEnabled && isLocalhost) {
      Logger.debug(
        "Development mode: OAuth bypass enabled - Bypassing authentication check for localhost and auto-authenticating for localhost",
        ["AuthService"],
      );
      return true;
    }

    const isAuth = !!(user && !user.expired);
    Logger.debug(
      `${isLocalhost ? "Development mode with OAuth enforcement" : "Production authentication check"}: ${isAuth ? "authenticated" : "not authenticated"}`,
      ["AuthService"],
    );
    return isAuth;
  }

  static async performSignIn(
    signinRedirect: () => Promise<void>,
  ): Promise<void> {
    Logger.info("Initiating OIDC sign-in process", ["AuthService"]);
    try {
      await signinRedirect();
      Logger.info("OIDC sign-in redirect initiated successfully", [
        "AuthService",
      ]);
    } catch (err) {
      Logger.error("OIDC sign-in process failed", err, ["AuthService"]);
      throw err;
    }
  }

  static async performSignOut(
    signoutRedirect: () => Promise<void>,
  ): Promise<void> {
    Logger.info("Initiating OIDC sign-out process", ["AuthService"]);
    try {
      await signoutRedirect();
      Logger.info("OIDC sign-out redirect initiated successfully", [
        "AuthService",
      ]);
    } catch (err) {
      Logger.error("OIDC sign-out process failed", err, ["AuthService"]);
      throw err;
    }
  }

  static async performSilentSignIn(
    signinSilent: () => Promise<OidcUser | null>,
  ): Promise<void> {
    Logger.info("Attempting silent token renewal via OIDC", ["AuthService"]);
    try {
      const user = await signinSilent();
      Logger.info(
        `Silent token renewal completed: ${user ? "successful" : "failed"}`,
        ["AuthService"],
      );
    } catch (err) {
      Logger.error("Silent token renewal failed", err, ["AuthService"]);
      throw err;
    }
  }

  static async performClearStaleState(
    clearStaleState: () => Promise<void>,
  ): Promise<void> {
    try {
      Logger.info("Clearing stale state", ["AuthService"]);
      await clearStaleState();
      Logger.info("Stale state cleared", ["AuthService"]);
    } catch (err) {
      Logger.error("Clearing stale state failed", err, ["AuthService"]);
      throw err;
    }
  }

  static hasRole(userRoles: AppRole[], requiredRole: AppRole): boolean {
    const hasRole = userRoles.includes(requiredRole);
    Logger.debug(
      `Role check: ${requiredRole} - ${hasRole ? "granted" : "denied"}`,
      ["AuthService"],
    );
    return hasRole;
  }

  static hasAnyRole(userRoles: AppRole[], requiredRoles: AppRole[]): boolean {
    const hasAnyRole = requiredRoles.some((role) => userRoles.includes(role));
    Logger.debug(`Multi-role check: ${hasAnyRole ? "granted" : "denied"}`, [
      "AuthService",
    ]);
    return hasAnyRole;
  }

  static isAdmin(userRoles: AppRole[]): boolean {
    const isAdmin = this.hasRole(userRoles, AppRole.SCRM_APP_ADMIN);
    Logger.debug(`Admin check: ${isAdmin ? "is admin" : "not admin"}`, [
      "AuthService",
    ]);
    return isAdmin;
  }

  static isReadOnlyUser(userRoles: AppRole[]): boolean {
    const isReadOnly = this.hasRole(userRoles, AppRole.SCRM_APP_READ_ONLY);
    Logger.debug(
      `Read-only check: ${isReadOnly ? "read-only" : "not read-only"}`,
      ["AuthService"],
    );
    return isReadOnly;
  }
}
